import React, { useState } from 'react';
import { MDBBtn } from 'mdb-react-ui-kit';
import CompleteOrder from '../CompleteOrder';
import moment from 'moment';
import Modal from '../../Modal';
import AssignedPhotographersDisplay from './AssignedPhotographersDisplay';

// Change 91: total column count for the alerts expansion <tr colSpan>.
// 1 (chevron) + 6 data columns (Client Name [+ Order ID], Services [+ Category],
// Date & Time, Order Status, Assigned To, Action) = 7. Update if the header row changes.
const TOTAL_COLUMNS = 7;

// Change 91: backend media base for selfie alert thumbnails. Keep in sync with Home.tsx.
const BACKEND_MEDIA_BASE = 'https://api.snapwiz.in';

/** Client-reported: wants the Order ID shown as "(date-id)" rather than the
 * raw 24-char Mongo ObjectId. This is purely a DISPLAY reformat — the actual
 * `receipt_id` used for matching/Razorpay/etc. is untouched — so it derives
 * a short date-prefixed label from the existing id + createdAt instead of
 * changing the underlying generation format (which is a business-critical
 * key used across payments, matching, and background jobs). */
function formatOrderIdForDisplay(receiptId: string | undefined, createdAt: string | undefined): string {
    if (!receiptId) return '-';
    const date = createdAt ? new Date(createdAt) : null;
    const datePart = date && !Number.isNaN(date.getTime())
        ? `${String(date.getDate()).padStart(2, '0')}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getFullYear()).slice(2)}`
        : null;
    const shortId = String(receiptId).slice(-6).toUpperCase();
    return datePart ? `${datePart}-${shortId}` : shortId;
}

/** Client-reported: "Services" column should show the actual service(s) on
 * the order, not the customer's booking category — replaces the old
 * Category-only column (category now shows as sub-text underneath). */
function summarizeServices(item: any): string {
    const slots = Array.isArray(item?.service_slots) ? item.service_slots : [];
    const labels = slots.length
        ? slots.map((s: any) => s?.specialization_required || s?.service_type).filter(Boolean)
        : Object.keys(item?.services || {}).filter((k) => item.services[k]);
    const unique = [...new Set(labels)];
    return unique.length ? unique.join(', ') : '-';
}

const RenderItem = ({ toggleOpen, item, redirectToAvailability, unassignAndRedirectToAvailability, completeOrder, cancelOrder, resolveAlert, isMainAdmin = true }: any) => {
    const [completeModalOpen, setCompleteModal] = useState(false);
    const [cancelModalOpen, setCancelModal] = useState(false);
    const [reassignModalOpen, setReassignModal] = useState(false);
    const [reassignServiceType, setReassignServiceType] = useState<string | null>(null);
    // Change 91: per-order alerts expansion state. Defaults to collapsed.
    const [alertsOpen, setAlertsOpen] = useState(false);
    const alerts: any[] = Array.isArray(item?.alerts) ? item.alerts : [];
    const alertCount = alerts.length;

    const toggleCompleteModal = () => {
        setCompleteModal(!completeModalOpen);
    }
    const closeCompleteModal = () => setCompleteModal(false);

    const toggleCancelModal = () => {
        setCancelModal(!cancelModalOpen);
    }
    const closeCancelModal = () => setCancelModal(false);

    // Slots that currently have a photographer on them — what the admin
    // picks between when the order has more than one service.
    const assignedSlots = (Array.isArray(item?.service_slots) ? item.service_slots : [])
        .filter((s: any) => s?.photographer_assigned?.userId);

    const openReassignModal = () => {
        // Default to the first assigned slot so a single click on "Yes" still
        // does the right thing for the common single-service case.
        setReassignServiceType(assignedSlots[0]?.service_type || null);
        setReassignModal(true);
    };
    const closeReassignModal = () => setReassignModal(false);
    const confirmReassign = () => {
        // Only pass service_type through when there's genuinely a choice —
        // keeps single-service orders on the original whole-order unassign path.
        const serviceType = assignedSlots.length > 1 ? reassignServiceType : undefined;
        unassignAndRedirectToAvailability(item?.receipt_id, serviceType);
        setReassignModal(false);
    };

    const submitCompleteOrder = async (storageLink: string, receiptId: string) => {
        await completeOrder(storageLink, receiptId);
        setCompleteModal(false);
    }

    const submitCancelOrder = async () => {
        await cancelOrder(item?.receipt_id);
        setCancelModal(false);
    }

    const eventFromMs = item?.eventTime?.from != null ? new Date(item.eventTime.from).getTime() : NaN;
    const eventStartedOrPast = Number.isFinite(eventFromMs) && Date.now() >= eventFromMs;

    if (!item?.receipt_id) {
        return <></>;
    }

    {
        return (
            <>
                <tr id="__clickableTableRow">
                    {/* Change 91: chevron toggle column. Disabled (no chevron, no click) when there are no alerts. */}
                    <td
                        style={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            cursor: alertCount > 0 ? 'pointer' : 'default',
                            padding: '0.75rem 0.25rem',
                            width: 36,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (alertCount > 0) setAlertsOpen(o => !o);
                        }}
                        title={alertCount > 0 ? `${alertCount} alert${alertCount === 1 ? '' : 's'} for this order` : ''}
                    >
                        {alertCount > 0 ? (
                            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative', width: 28, height: 28, borderRadius: 6, backgroundColor: 'rgba(192,120,120,0.12)' }}>
                                <i className={`mdi ${alertsOpen ? 'mdi-chevron-down' : 'mdi-chevron-right'}`} style={{ fontSize: 18, color: '#c07878', lineHeight: 1 }} />
                                <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, backgroundColor: '#c07878', color: '#fff', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>{alertCount}</span>
                            </div>
                        ) : (
                            <span style={{ display: 'inline-block', width: 28, height: 28 }} />
                        )}
                    </td>
                    <td onClick={() => { toggleOpen(item) }} style={{ cursor: "pointer" }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src="assets/images/profile.png" alt="image" />
                            <div className="ps-2" style={{ display: 'flex', flexDirection: 'column' }}>
                                <span>{item?.forUserName || "-"}</span>
                                <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', opacity: 0.65 }}>
                                    {formatOrderIdForDisplay(item?.receipt_id, item?.createdAt)}
                                </span>
                            </div>
                        </div>
                    </td>
                    <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{summarizeServices(item)}</span>
                            {item?.categoryData?.name ? (
                                <span style={{ fontSize: '0.75rem', opacity: 0.65 }}>{item.categoryData.name}</span>
                            ) : null}
                        </div>
                    </td>
                    <td>
                        <div>{moment(new Date(item?.eventTime?.from)).format('MMMM Do YYYY')}</div>
                        <div style={{ fontSize: '0.85rem', opacity: 0.75 }}>{moment(new Date(item?.eventTime?.from)).format('hh:mm a')} - {moment(new Date(item?.eventTime?.till)).format('hh:mm a')}</div>
                    </td>
                    <td>
                        {item?.status === "pending" ? <div className="badge badge-outline-info">Pending / Failed</div> : null}
                        {item?.status === "paid" ? (item?.photographer_assigned?.isOrderAccepted ? <div className="badge badge-outline-primary">Upcoming</div> : <div className="badge badge-outline-secondary">Matching</div>) : null}
                        {item?.status === "ongoing" ? <div className="badge badge-outline-primary">Ongoing</div> : null}
                        {item?.status === "processing" ? <div className="badge badge-outline-secondary">Processing</div> : null}
                        {item?.status === "completed" ? <div className="badge badge-outline-success">Completed</div> : null}
                        {item?.status === "refund_processing" ? <div className="badge badge-outline-tertiary">Refund Initiated</div> : null}
                        {item?.status === "refunded" ? <div className="badge badge-outline-warning">Refunded</div> : null}
                    </td>
                    <td style={{ verticalAlign: 'top' }}>
                        <AssignedPhotographersDisplay item={item} variant="table" />
                    </td>
                    <td style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        {!isMainAdmin ? (
                            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem' }}>View only</span>
                        ) : item?.cancelled_by_customer ? (
                            <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.875rem', lineHeight: 1.4 }}>
                                Order cancelled by customer
                            </span>
                        ) : (
                            <>
                                {item?.status === "processing" ? <MDBBtn onClick={(e) => { e.stopPropagation(); toggleCompleteModal(); }} color='primary' rippleColor='light'>Complete Order</MDBBtn> :
                                    (item?.payment_status === "paid" && (item?.status !== "completed" && !item?.status.includes("refund")) && !item?.photographer_assigned?.isOrderAccepted && !eventStartedOrPast) ? <MDBBtn disabled={item?.photographer_assigned?.isOrderAccepted} onClick={(e) => { e.stopPropagation(); redirectToAvailability(item?.receipt_id); }} color='primary' rippleColor='light'>{item?.photographer_assigned?.isOrderAccepted ? "Assigned" : 'Request Photographer'}</MDBBtn>
                                        : null}

                                {/* Client-reported: Reassign should disappear once the shoot has
                                    started — previously stayed visible for "ongoing" orders too. */}
                                {(item?.status !== "completed" && (item?.status !== "refunded" && item?.status !== "refund_processing") && item?.status !== "ongoing" && item?.photographer_assigned?.isOrderAccepted && !eventStartedOrPast) ? <MDBBtn onClick={(e) => { e.stopPropagation(); openReassignModal(); }} color='secondary' rippleColor='light'>Reassign</MDBBtn> : null}

                                {item?.status !== "completed" && (((item?.status !== "refunded" && item?.status !== "refund_processing")) && !item?.photographer_assigned?.isOrderAccepted) ? <MDBBtn onClick={(e) => { e.stopPropagation(); toggleCancelModal(); }} color='danger' rippleColor='light'>Cancel</MDBBtn> :
                                    (item?.status !== "completed" && (((item?.status !== "refunded" && item?.status !== "refund_processing")) && (item?.photographer_assigned?.isOrderAccepted))) ? <MDBBtn onClick={(e) => { e.stopPropagation(); toggleCancelModal(); }} color='danger' rippleColor='light'>Cancel</MDBBtn>
                                        : <MDBBtn color='danger' rippleColor='light' style={{ visibility: "hidden" }}>Placeholder</MDBBtn>}
                            </>
                        )}
                    </td>
                </tr>
                {/* Change 91: expanded per-order alerts row. Shown only when chevron is open AND alerts exist. */}
                {alertsOpen && alertCount > 0 ? (
                    <tr id="__orderAlertsExpansion" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                        <td colSpan={TOTAL_COLUMNS} style={{ padding: '12px 18px 16px 18px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                <i className="mdi mdi-bell-alert-outline" style={{ fontSize: 14, color: '#c07878' }} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#d4d4d4', letterSpacing: 0.3 }}>Alerts for this order ({alertCount})</span>
                                <span style={{ fontSize: 11, color: '#666', fontFamily: 'monospace' }}>#{item?.receipt_id}</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {alerts.map((alert: any) => {
                                    const isHigh = alert?.priority === 'high';
                                    const isMed = alert?.priority === 'medium';
                                    const accentColor = isHigh ? '#c07878' : isMed ? '#b89a5a' : '#6a92b8';
                                    const rowBg = isHigh ? 'rgba(192,120,120,0.07)' : isMed ? 'rgba(184,154,90,0.07)' : 'rgba(106,146,184,0.07)';
                                    const badgeBg = isHigh ? 'rgba(192,120,120,0.15)' : isMed ? 'rgba(184,154,90,0.15)' : 'rgba(106,146,184,0.15)';
                                    const iconName =
                                        alert?.type === 'photographer_cancel' ? 'mdi-account-off-outline'
                                        : (alert?.type === 'radius_expansion' || alert?.type === 'v2_radius_expansion') ? 'mdi-map-marker-radius-outline'
                                        : alert?.type === 'start_selfie_submitted' ? 'mdi-camera-outline'
                                        : alert?.type === 'manual_assignment_needed' ? 'mdi-account-question-outline'
                                        : alert?.type === 'customer_cancel_refund' ? 'mdi-cash-refund'
                                        : alert?.type === 'order_unpaid_timeout' ? 'mdi-clock-alert-outline'
                                        : alert?.type === 'event_passed_auto_expire' ? 'mdi-calendar-remove-outline'
                                        : 'mdi-alert-outline';
                                    const selfieFullUrl = alert?.selfie_url?.startsWith?.('http') ? alert.selfie_url : (alert?.selfie_url ? `${BACKEND_MEDIA_BASE}${alert.selfie_url}` : null);
                                    return (
                                        <div key={alert?._id} style={{
                                            backgroundColor: rowBg,
                                            borderLeft: `3px solid ${accentColor}`,
                                            borderRadius: 6,
                                            padding: '10px 14px',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 12,
                                        }}>
                                            <div style={{
                                                width: 28, height: 28, borderRadius: 6,
                                                backgroundColor: badgeBg,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0, marginTop: 1,
                                            }}>
                                                <i className={`mdi ${iconName}`} style={{ fontSize: 14, color: accentColor }} />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#d4d4d4', lineHeight: 1.4 }}>{alert?.title}</p>
                                                <p style={{ margin: '3px 0 0', fontSize: 12, color: '#858585', lineHeight: 1.5 }}>{alert?.message}</p>
                                                {(alert?.photographer_name || alert?.photographer_contact) ? (
                                                    <p style={{ margin: '4px 0 0', fontSize: 11, color: '#7d7d7d' }}>
                                                        <i className="mdi mdi-account-outline" style={{ fontSize: 11, marginRight: 4 }} />
                                                        {alert?.photographer_name || ''}{alert?.photographer_contact ? ` · ${alert.photographer_contact}` : ''}
                                                    </p>
                                                ) : null}
                                                {alert?.type === 'start_selfie_submitted' && selfieFullUrl ? (
                                                    <div style={{ marginTop: 8 }}>
                                                        <img
                                                            src={selfieFullUrl}
                                                            alt="Start selfie"
                                                            style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
                                                            onClick={(e) => { e.stopPropagation(); window.open(selfieFullUrl, '_blank'); }}
                                                        />
                                                        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                            {alert?.selfie_submitted_at ? (
                                                                <p style={{ margin: 0, fontSize: 11, color: '#858585' }}>
                                                                    <i className="mdi mdi-clock-outline" style={{ fontSize: 11, marginRight: 4 }} />
                                                                    {moment(new Date(alert.selfie_submitted_at)).format('DD MMM YYYY, hh:mm:ss A')}
                                                                </p>
                                                            ) : null}
                                                            {(alert?.selfie_lat != null || alert?.selfie_lng != null) ? (
                                                                <p style={{ margin: 0, fontSize: 11, color: '#858585' }}>
                                                                    <i className="mdi mdi-map-marker-outline" style={{ fontSize: 11, marginRight: 4 }} />
                                                                    {Number(alert.selfie_lat).toFixed(6)}, {Number(alert.selfie_lng).toFixed(6)}
                                                                    <a
                                                                        href={`https://www.google.com/maps?q=${alert.selfie_lat},${alert.selfie_lng}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        style={{ marginLeft: 6, fontSize: 10, color: '#6a92b8', textDecoration: 'none' }}
                                                                    >
                                                                        View on Map ↗
                                                                    </a>
                                                                </p>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0, minWidth: 90 }}>
                                                <span style={{
                                                    fontSize: 10, fontWeight: 700, letterSpacing: '0.5px',
                                                    textTransform: 'uppercase', color: accentColor,
                                                    backgroundColor: badgeBg, padding: '2px 8px', borderRadius: 10,
                                                }}>
                                                    {alert?.priority}
                                                </span>
                                                <p style={{ margin: 0, fontSize: 11, color: '#666' }}>{alert?.createdAt ? moment(new Date(alert.createdAt)).fromNow() : ''}</p>
                                                {!alert?.resolved && resolveAlert && isMainAdmin ? (
                                                    <button
                                                        style={{
                                                            fontSize: 11, padding: '3px 10px', borderRadius: 4,
                                                            border: '1px solid rgba(255,255,255,0.12)',
                                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                                            color: '#9e9e9e', cursor: 'pointer', marginTop: 2,
                                                        }}
                                                        onMouseEnter={e => { (e.target as any).style.backgroundColor = 'rgba(255,255,255,0.1)'; (e.target as any).style.color = '#d4d4d4'; }}
                                                        onMouseLeave={e => { (e.target as any).style.backgroundColor = 'rgba(255,255,255,0.05)'; (e.target as any).style.color = '#9e9e9e'; }}
                                                        onClick={(e) => { e.stopPropagation(); resolveAlert(alert?._id, item?.receipt_id); }}
                                                    >
                                                        Resolve
                                                    </button>
                                                ) : null}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </td>
                    </tr>
                ) : null}
                {(completeModalOpen && (item?.payment_status === "paid")) ? <CompleteOrder toggleOpen={closeCompleteModal} orderData={item} completeOrder={submitCompleteOrder} /> : null}
                <Modal open={cancelModalOpen} title='Cancel Order' onConfirm={submitCancelOrder} toggleOpen={closeCancelModal} content={<h6>Are you sure about cancelling the order?</h6>} />
                <Modal
                    open={reassignModalOpen}
                    title='Reassign Photographer'
                    onConfirm={confirmReassign}
                    toggleOpen={closeReassignModal}
                    content={
                        assignedSlots.length > 1 ? (
                            <>
                                <h6 style={{ marginBottom: 14 }}>This order has multiple services. Which one do you want to reassign?</h6>
                                {assignedSlots.map((slot: any) => {
                                    const label = slot?.specialization_required || slot?.service_type;
                                    const photographerName = slot?.photographer_assigned?.name;
                                    return (
                                        <label
                                            key={slot?.service_type}
                                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer' }}
                                        >
                                            <input
                                                type="radio"
                                                name="reassign-service-type"
                                                checked={reassignServiceType === slot?.service_type}
                                                onChange={() => setReassignServiceType(slot?.service_type)}
                                            />
                                            <span>{label}{photographerName ? ` — currently ${photographerName}` : ''}</span>
                                        </label>
                                    );
                                })}
                            </>
                        ) : (
                            <h6>Are you sure you want to reassign this order to another photographer?</h6>
                        )
                    }
                />
            </>
        );
    }
}

export default RenderItem;